import { useState, useEffect, useRef, useMemo } from "react";
import OnboardingBanner from "@/components/OnboardingBanner";
import api from "@/lib/api";
import { formatINR, PROPERTY_TYPES, AMENITIES_LIST, INDIA_CITIES } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  MagnifyingGlass, TrendUp, TrendDown, ShieldCheck, Warning, Lightning,
  Sparkle, Upload, X, Clock, Trash, Image, Star, ArrowRight,
  House, Key, Scroll, CheckSquare, Square, ArrowsLeftRight, Globe
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TOOLTIP_STYLE = { backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", fontSize: "11px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" };


const ACQUISITION_TYPES = [
  { id: "purchase", label: "Purchase", icon: House,  desc: "You buy the property outright",         color: "amber"  },
  { id: "rental",   label: "Rental",   icon: Key,    desc: "Monthly rent + security deposit",        color: "sky"    },
  { id: "lease",    label: "Lease",    icon: Scroll, desc: "Lump sum for a fixed term (1→5 yrs)",   color: "purple" },
];

const METRIC_DESCRIPTIONS = {
  projected_monthly_revenue: { label: "Monthly Revenue",       desc: "Projected Airbnb earnings at estimated occupancy" },
  estimated_occupancy:       { label: "Occupancy",             desc: "% of nights expected to be booked based on local demand" },
  area_adr:                  { label: "Area ADR",              desc: "Average nightly rate charged by similar listings nearby" },
  recommended_nightly_rate:  { label: "Rec. Rate/Night",       desc: "AI-suggested optimal price to maximise bookings & revenue" },
  estimated_annual_tax:      { label: "Annual Tax",            desc: "Estimated income tax liability on Airbnb rental earnings" },
  competitive_score:         { label: "Competitive Score",     desc: "How well this property stacks up against local competition (0→100)" },
  roi_estimate:              { label: "ROI",                   desc: "Annual profit ÷ total capital invested × 100" },
  payback_period_months:     { label: "Payback Period",        desc: "Months needed to fully recover your purchase investment" },
  first_year_net_profit:     { label: "Year 1 Net Profit",     desc: "Revenue minus all costs in first year" },
  monthly_surplus:           { label: "Monthly Surplus",       desc: "Airbnb revenue minus rent and operating expenses per month" },
  breakeven_occupancy:       { label: "Breakeven Occupancy",   desc: "Minimum occupancy % required to cover rent + expenses" },
  effective_monthly_cost:    { label: "Effective Monthly Cost",desc: "Lump-sum lease amount spread across total lease months" },
  lease_recovery_months:     { label: "Lease Recovery",        desc: "Months of Airbnb profit needed to recover the lease lump sum" },
};

// ── Small reusable components ────────────────────────────────────────────────

function MetricCard({ key_, value, color = "text-slate-800 dark:text-slate-100" }) {
  const meta = METRIC_DESCRIPTIONS[key_] || { label: key_, desc: "" };
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 rounded-xl p-3 shadow-sm">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{meta.label}</p>
      {meta.desc && <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{meta.desc}</p>}
      <p className={`font-mono text-base font-semibold mt-1.5 ${color}`}>{value}</p>
    </div>
  );
}

function ScoreRing({ score, max = 10, size = 120 }) {
  const radius = (size - 20) / 2;
  const circ = 2 * Math.PI * radius;
  const pct = Math.min(Math.max(score / max, 0), 1);
  const color = score >= 7 ? "#22c55e" : score >= 5 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={8} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={`${pct * circ} ${circ}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-2xl font-extrabold text-slate-800 dark:text-white">{score}</span>
        <span className="text-[10px] text-slate-400 font-medium">/ {max}</span>
      </div>
    </div>
  );
}

function ScoreBar({ label, value, max = 10 }) {
  const pct = Math.min(Math.max((value / max) * 100, 0), 100);
  const color = value >= 7 ? "bg-emerald-400" : value >= 5 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-700 dark:text-slate-200 font-medium capitalize">{label.replace(/_/g, " ")}</span>
        <span className="text-slate-500 dark:text-slate-400 font-mono">{value}/{max}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function VerdictBadge({ verdict, size = "sm" }) {
  if (!verdict) return null;
  const cfg = {
    "Go":    { cls: "bg-emerald-50 text-emerald-600 border-emerald-200" },
    "Hold":  { cls: "bg-amber-50 text-amber-600 border-amber-200" },
    "No-Go": { cls: "bg-red-50 text-red-500 border-red-200" },
  }[verdict] || { cls: "bg-slate-50 text-slate-500 border-slate-200" };
  return (
    <Badge variant="outline" className={`${cfg.cls} ${size === "lg" ? "text-sm px-3 py-1 font-bold" : "text-[10px] px-2 py-0.5 font-semibold"}`}>
      {verdict}
    </Badge>
  );
}

function ImageUploader({ images, onAdd, onRemove }) {
  const inputRef = useRef(null);
  const handleFiles = (files) => {
    const valid = Array.from(files).filter(f => f.type.startsWith("image/"));
    if (images.length + valid.length > 10) { toast.error("Maximum 10 images"); return; }
    onAdd(valid);
  };
  return (
    <div className="space-y-3">
      <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Property Photos (optional, up to 10)</Label>
      {images.length < 10 && (
        <div onClick={() => inputRef.current?.click()}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-slate-200 rounded-xl p-5 text-center cursor-pointer hover:border-amber-300 hover:bg-amber-50/30 transition-colors">
          <Upload size={22} className="text-slate-300 mx-auto mb-1.5" />
          <p className="text-slate-500 dark:text-slate-300 text-xs">Drop images here or <span className="text-amber-500 font-medium">browse</span></p>
          <p className="text-slate-400 text-[10px] mt-0.5">JPG, PNG, WEBP · max 10 images</p>
          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
        </div>
      )}
      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((img, i) => (
            <div key={i} className="relative group aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
              {img.preview
                ? <img src={img.preview} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><Image size={20} className="text-slate-300" /></div>}
              {img.uploading && <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><div className="spinner" style={{ width: 16, height: 16 }} /></div>}
              {!img.uploading && (
                <button onClick={() => onRemove(i)} className="absolute top-1 right-1 w-5 h-5 bg-white rounded-full shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50">
                  <X size={10} className="text-slate-300" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── History card ─────────────────────────────────────────────────────────────

function HistoryCard({ ev, onView, onDelete, selected, compareChecked, onCompareToggle }) {
  const d = ev.property_details || {};
  const city = ev.city || d.city || "Unknown";
  const neighbourhood = ev.neighbourhood || d.neighbourhood || "";
  const acqLabel = { purchase: "Purchase", rental: "Rental", lease: "Lease" }[d.acquisition_type] || "Purchase";
  const date = new Date(ev.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return (
    <Card className={`border rounded-xl shadow-sm transition-all ${selected ? "border-amber-400 bg-amber-50/20 dark:bg-amber-900/10" : "bg-white dark:bg-slate-800 border-slate-200 hover:shadow-md"}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <button onClick={onCompareToggle} className="mt-0.5 shrink-0 text-slate-300 hover:text-amber-400 transition-colors">
            {compareChecked
              ? <CheckSquare size={16} className="text-amber-500" weight="fill" />
              : <Square size={16} />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-slate-800 dark:text-white text-sm truncate">
                {neighbourhood ? `${neighbourhood}, ` : ""}{city}
              </p>
              {ev.verdict && <VerdictBadge verdict={ev.verdict} />}
              {ev.property_score != null && <span className="font-mono text-xs text-slate-400">{ev.property_score}/10</span>}
            </div>
            <p className="text-slate-400 text-xs mt-0.5">{d.property_type} · {d.bedrooms} BR · {acqLabel} · {date}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => onView(ev)} className="text-amber-500 hover:text-amber-600 p-1 rounded-lg hover:bg-amber-50 transition-colors">
              <ArrowRight size={16} />
            </button>
            <button onClick={() => onDelete(ev.id)} className="text-slate-400 hover:text-red-500 p-1 rounded-lg hover:bg-red-50 transition-colors">
              <Trash size={15} />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Full results panel (reused in new tab + history detail) ──────────────────

function ResultsPanel({ result, acqType }) {
  if (!result || result.error) return null;
  const acq = result.acquisition_type || acqType;
  const verdictColor = result.verdict === "Go" ? "text-emerald-600" : result.verdict === "Hold" ? "text-amber-500" : "text-red-500";
  const verdictBg    = result.verdict === "Go"
    ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700"
    : result.verdict === "Hold"
    ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700"
    : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700";

  const sharedMetrics = [
    { key_: "projected_monthly_revenue", value: formatINR(result.projected_monthly_revenue), color: "text-amber-500" },
    { key_: "estimated_occupancy",       value: `${result.estimated_occupancy}%`,            color: "text-sky-500" },
    { key_: "recommended_nightly_rate",  value: formatINR(result.recommended_nightly_rate),  color: "text-amber-600" },
    { key_: "area_adr",                  value: formatINR(result.area_adr),                  color: "text-slate-700 dark:text-slate-200" },
    { key_: "estimated_annual_tax",      value: formatINR(result.estimated_annual_tax),      color: "text-red-500" },
  ];
  const scenarioMetrics = acq === "purchase"
    ? [
        { key_: "roi_estimate",          value: `${result.roi_estimate}%`,                        color: "text-emerald-600" },
        { key_: "payback_period_months", value: `${result.payback_period_months} mo`,              color: "text-purple-500" },
        { key_: "first_year_net_profit", value: formatINR(result.first_year_net_profit),           color: result.first_year_net_profit >= 0 ? "text-emerald-600" : "text-red-500" },
      ]
    : acq === "rental"
    ? [
        { key_: "monthly_surplus",       value: formatINR(result.monthly_surplus),                 color: result.monthly_surplus >= 0 ? "text-emerald-600" : "text-red-500" },
        { key_: "breakeven_occupancy",   value: `${result.breakeven_occupancy}%`,                  color: "text-amber-500" },
        { key_: "first_year_net_profit", value: formatINR(result.first_year_net_profit),           color: result.first_year_net_profit >= 0 ? "text-emerald-600" : "text-red-500" },
      ]
    : [
        { key_: "effective_monthly_cost",  value: formatINR(result.effective_monthly_cost),        color: "text-purple-500" },
        { key_: "lease_recovery_months",   value: `${result.lease_recovery_months} mo`,            color: "text-amber-500" },
        { key_: "first_year_net_profit",   value: formatINR(result.first_year_net_profit),         color: result.first_year_net_profit >= 0 ? "text-emerald-600" : "text-red-500" },
      ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {result.market_data_used && (
          <div className="flex items-center gap-1.5 text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 font-medium">
            <Globe size={11} /> Live Airbnb market data
          </div>
        )}
        {result.ml_pred_used && (
          <div className="flex items-center gap-1.5 text-[10px] text-sky-600 bg-sky-50 border border-sky-200 rounded-lg px-3 py-1.5 font-medium">
            <Sparkle size={11} /> HostFolio ML model
          </div>
        )}
      </div>

      {/* Score + Verdict */}
      <Card className={`border rounded-2xl ai-shimmer shadow-sm ${verdictBg}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            {typeof result.property_score === "number" && (
              <div className="text-center shrink-0">
                <ScoreRing score={result.property_score} />
                <p className="text-[10px] text-slate-400 mt-1 font-semibold uppercase tracking-widest">Score</p>
              </div>
            )}
            <div className="flex-1 text-center">
              <p className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1 font-semibold">Verdict</p>
              <p className={`font-manrope text-4xl font-bold ${verdictColor}`}>{result.verdict}</p>
              <p className="text-sm text-slate-700 dark:text-slate-200 mt-2">{result.justification}</p>
            </div>
          </div>
          {result.score_breakdown && (
            <div className="mt-4 space-y-2 pt-4 border-t border-slate-200/60">
              {Object.entries(result.score_breakdown).map(([k, v]) => <ScoreBar key={k} label={k} value={v} />)}
            </div>
          )}
        </CardContent>
      </Card>

      {result.image_observations && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-xl shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-widest mb-2 font-semibold flex items-center gap-1"><Star size={12} /> Photo Analysis</p>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{result.image_observations}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3">
        {[...scenarioMetrics, ...sharedMetrics].map(m => <MetricCard key={m.key_} {...m} />)}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-xl shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-widest mb-1 font-semibold">Risk Level</p>
            <p className="text-[10px] text-slate-400 mb-2">Based on market competition, location & financial exposure</p>
            <Badge variant="outline" className={result.risk_level === "Low" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : result.risk_level === "Medium" ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-red-50 text-red-500 border-red-200"}>
              {result.risk_level === "Low" ? <ShieldCheck size={14} className="mr-1" /> : <Warning size={14} className="mr-1" />}{result.risk_level}
            </Badge>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-xl shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-widest mb-1 font-semibold">Competitive Score</p>
            <p className="text-[10px] text-slate-400 mb-2">vs similar listings in the area</p>
            <p className="font-mono text-2xl font-semibold text-amber-500">{result.competitive_score}<span className="text-sm text-slate-400">/100</span></p>
          </CardContent>
        </Card>
      </div>

      {result.seasonal_demand && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-2xl shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700 font-semibold">Seasonal Demand</CardTitle></CardHeader>
          <CardContent>
            <p className="text-[10px] text-slate-400 mb-2">Month-by-month booking demand (1→10) based on local tourism & business travel</p>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={result.seasonal_demand}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={10} tickLine={false} />
                <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} domain={[0, 10]} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="level" fill="#E8B931" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {result.strengths?.length > 0 && (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-xl shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-emerald-600 uppercase tracking-widest mb-2 flex items-center gap-1 font-semibold"><TrendUp size={12} /> Strengths</p>
              <ul className="space-y-1.5">{result.strengths.map((s, i) => <li key={i} className="text-xs text-slate-700 dark:text-slate-200 flex items-start gap-2"><span className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 shrink-0" />{s}</li>)}</ul>
            </CardContent>
          </Card>
        )}
        {result.risks?.length > 0 && (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-xl shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-red-500 uppercase tracking-widest mb-2 flex items-center gap-1 font-semibold"><TrendDown size={12} /> Risks</p>
              <ul className="space-y-1.5">{result.risks.map((r, i) => <li key={i} className="text-xs text-slate-700 dark:text-slate-200 flex items-start gap-2"><span className="w-1 h-1 rounded-full bg-red-400 mt-1.5 shrink-0" />{r}</li>)}</ul>
            </CardContent>
          </Card>
        )}
      </div>

      {result.competitor_analysis && (
        <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-xl shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-widest mb-1 font-semibold">Competitor Benchmarking</p>
            <p className="text-[10px] text-slate-400 mb-3">Comparable Airbnb listings in the same area</p>
            <div className="grid grid-cols-2 gap-3 text-center">
              {[
                ["Avg Nightly Rate", formatINR(result.competitor_analysis.avg_nightly_rate)],
                ["Avg Occupancy",    `${result.competitor_analysis.avg_occupancy}%`],
                ["Avg Rating",       result.competitor_analysis.avg_rating],
                ["Total Listings",   result.competitor_analysis.total_listings],
              ].map(([label, val]) => (
                <div key={label}>
                  <p className="font-mono text-lg text-slate-800 dark:text-slate-100 font-semibold">{val}</p>
                  <p className="text-[10px] text-slate-400">{label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Side-by-side comparison panel ────────────────────────────────────────────

function ComparePanel({ data, onClose }) {
  const [ev1, ev2] = data;
  const r1 = ev1.result || {};
  const r2 = ev2.result || {};
  const d1 = ev1.property_details || {};
  const d2 = ev2.property_details || {};
  const loc1 = [ev1.neighbourhood || d1.neighbourhood, ev1.city || d1.city].filter(Boolean).join(", ");
  const loc2 = [ev2.neighbourhood || d2.neighbourhood, ev2.city || d2.city].filter(Boolean).join(", ");

  const rows = [
    { label: "Property Score",    v1: r1.property_score,          v2: r2.property_score,          fmt: v => `${v}/10`,      higher: true  },
    { label: "Monthly Revenue",   v1: r1.projected_monthly_revenue,v2: r2.projected_monthly_revenue,fmt: formatINR,          higher: true  },
    { label: "Occupancy %",       v1: r1.estimated_occupancy,     v2: r2.estimated_occupancy,     fmt: v => `${v}%`,        higher: true  },
    { label: "Rec. Nightly Rate", v1: r1.recommended_nightly_rate,v2: r2.recommended_nightly_rate,fmt: formatINR,           higher: null  },
    { label: "Area ADR",          v1: r1.area_adr,                v2: r2.area_adr,                fmt: formatINR,           higher: null  },
    { label: "First Year Profit", v1: r1.first_year_net_profit,   v2: r2.first_year_net_profit,   fmt: formatINR,           higher: true  },
    { label: "ROI %",             v1: r1.roi_estimate,            v2: r2.roi_estimate,            fmt: v => `${v}%`,        higher: true  },
    { label: "Competitive Score", v1: r1.competitive_score,       v2: r2.competitive_score,       fmt: v => `${v}/100`,     higher: true  },
    { label: "Risk Level",        v1: r1.risk_level,              v2: r2.risk_level,              fmt: v => v,              higher: "low" },
    { label: "Annual Tax",        v1: r1.estimated_annual_tax,    v2: r2.estimated_annual_tax,    fmt: formatINR,           higher: false },
  ];

  const winner = (row, idx) => {
    if (row.higher === null || row.v1 == null || row.v2 == null) return false;
    if (row.higher === "low") {
      const rank = { Low: 1, Medium: 2, High: 3 };
      return idx === 0 ? (rank[row.v1] || 2) < (rank[row.v2] || 2) : (rank[row.v2] || 2) < (rank[row.v1] || 2);
    }
    return row.higher ? (idx === 0 ? row.v1 > row.v2 : row.v2 > row.v1) : (idx === 0 ? row.v1 < row.v2 : row.v2 < row.v1);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ArrowsLeftRight size={18} className="text-amber-500" />
          <h2 className="font-manrope font-semibold text-slate-800 dark:text-white">Comparison</h2>
          <span className="text-xs text-slate-400">▲ highlights the better value</span>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {[{ loc: loc1, ev: ev1, d: d1 }, { loc: loc2, ev: ev2, d: d2 }].map(({ loc, ev, d }, idx) => (
          <Card key={idx} className="bg-white dark:bg-slate-800 border-slate-200 rounded-xl shadow-sm">
            <CardContent className="p-4 text-center">
              <VerdictBadge verdict={ev.verdict} size="lg" />
              <p className="font-semibold text-slate-800 dark:text-white text-sm mt-2 truncate">{loc || "Unknown"}</p>
              <p className="text-slate-400 text-xs mt-0.5">{d.property_type} · {d.bedrooms} BR · {d.acquisition_type}</p>
              {typeof ev.property_score === "number" && (
                <div className="mt-3 flex justify-center"><ScoreRing score={ev.property_score} size={80} /></div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-xl shadow-sm">
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {rows.map((row, i) => {
              const w1 = winner(row, 0);
              const w2 = winner(row, 1);
              return (
                <div key={i} className="grid grid-cols-[1fr,auto,1fr] items-center px-4 py-3">
                  <p className={`text-sm font-mono text-right pr-3 ${w1 ? "text-emerald-600 font-semibold" : "text-slate-700 dark:text-slate-200"}`}>
                    {row.v1 != null ? row.fmt(row.v1) : "—"}
                    {w1 && <span className="ml-1 text-[10px]">▲</span>}
                  </p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider text-center px-2 font-semibold whitespace-nowrap">{row.label}</p>
                  <p className={`text-sm font-mono text-left pl-3 ${w2 ? "text-emerald-600 font-semibold" : "text-slate-700 dark:text-slate-200"}`}>
                    {w2 && <span className="mr-1 text-[10px]">▲</span>}
                    {row.v2 != null ? row.fmt(row.v2) : "—"}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PropertyEvaluator() {
  const [tab, setTab]         = useState("new");
  const [acqType, setAcqType] = useState("purchase");
  const [form, setForm]       = useState({
    state: "", city: "", neighbourhood: "", pincode: "", property_type: "Apartment",
    bedrooms: "", nightly_rate: 0,
    purchase_price: "", monthly_rent: 0, security_deposit_months: 2,
    lease_amount: 0, lease_duration_years: 1, amenities: [],
  });
  const [images, setImages]   = useState([]);
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [user, setUser]       = useState(null);

  // History tab state
  const [selectedEval, setSelectedEval] = useState(null);
  const [compareIds, setCompareIds]     = useState([]);
  const [showCompare, setShowCompare]   = useState(false);
  const [compareData, setCompareData]   = useState([]);
  const [filterCity, setFilterCity]     = useState("all");
  const [filterArea, setFilterArea]     = useState("all");

  useEffect(() => { api.get("/auth/me").then(r => setUser(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab]); // eslint-disable-line

  const loadHistory = async () => {
    setHistoryLoading(true);
    try { const { data } = await api.get("/evaluations"); setHistory(data); }
    catch { toast.error("Could not load history"); }
    finally { setHistoryLoading(false); }
  };

  const availableCities = useMemo(() =>
    [...new Set(history.map(ev => ev.city || ev.property_details?.city).filter(Boolean))].sort()
  , [history]);

  const availableAreas = useMemo(() =>
    [...new Set(
      history
        .filter(ev => filterCity === "all" || (ev.city || ev.property_details?.city) === filterCity)
        .map(ev => ev.neighbourhood || ev.property_details?.neighbourhood)
        .filter(Boolean)
    )].sort()
  , [history, filterCity]);

  const filteredHistory = useMemo(() =>
    history.filter(ev => {
      const c = ev.city || ev.property_details?.city || "";
      const a = ev.neighbourhood || ev.property_details?.neighbourhood || "";
      if (filterCity !== "all" && c !== filterCity) return false;
      if (filterArea !== "all" && a !== filterArea) return false;
      return true;
    })
  , [history, filterCity, filterArea]);

  const toggleAmenity = (a) => setForm(f => ({
    ...f, amenities: f.amenities.includes(a) ? f.amenities.filter(x => x !== a) : [...f.amenities, a]
  }));

  const handleAddImages = async (files) => {
    const newImgs = files.map(f => ({
      id: Math.random().toString(36).slice(2),
      file: f, preview: URL.createObjectURL(f), uploading: true, done: false, storage_path: null,
    }));
    setImages(prev => [...prev, ...newImgs]);
    for (const img of newImgs) {
      const fd = new FormData();
      fd.append("file", img.file);
      try {
        const { data } = await api.post("/upload", fd);
        setImages(prev => prev.map(im => im.id === img.id ? { ...im, uploading: false, done: true, storage_path: data.url || data.storage_path } : im));
      } catch {
        toast.error(`Failed to upload ${img.file.name}`);
        setImages(prev => {
          const t = prev.find(im => im.id === img.id);
          if (t?.preview) URL.revokeObjectURL(t.preview);
          return prev.filter(im => im.id !== img.id);
        });
      }
    }
  };

  const handleRemoveImage = (i) => setImages(prev => {
    if (prev[i]?.preview) URL.revokeObjectURL(prev[i].preview);
    return prev.filter((_, idx) => idx !== i);
  });

  const handleEvaluate = async () => {
    if (!form.city) { toast.error("City is required"); return; }
    if (images.some(im => im.uploading)) { toast.error("Wait for images to finish uploading"); return; }
    setLoading(true); setResult(null);
    try {
      const image_paths = images.filter(im => im.done && im.storage_path).map(im => im.storage_path);
      const payload = { ...form, bedrooms: parseInt(form.bedrooms) || 2, purchase_price: parseFloat(form.purchase_price) || 0, acquisition_type: acqType, image_paths };
      const { data } = await api.post("/evaluator", payload);
      if (data.error) { toast.error(data.error); }
      else {
        setResult({ ...data, acquisition_type: acqType });
        if (typeof data.credits_remaining === "number" && data.credits_remaining !== 999999)
          setUser(u => u ? { ...u, eval_credits: data.credits_remaining } : u);
        toast.success("Analysis complete!");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Evaluation failed. Please try again.";
      toast.error(e?.response?.status === 402 ? "No evaluation credits remaining." : msg);
    } finally { setLoading(false); }
  };

  const handleViewHistory = async (ev) => {
    try {
      const { data } = await api.get(`/evaluations/${ev.id}`);
      setSelectedEval(data);
      setShowCompare(false);
    } catch { toast.error("Could not load evaluation"); }
  };

  const handleDeleteHistory = async (id) => {
    try {
      await api.delete(`/evaluations/${id}`);
      setHistory(h => h.filter(e => e.id !== id));
      if (selectedEval?.id === id) setSelectedEval(null);
      setCompareIds(c => c.filter(cid => cid !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const toggleCompare = (id) => {
    setCompareIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 2) { toast.error("Select only 2 evaluations to compare"); return prev; }
      return [...prev, id];
    });
  };

  const handleCompare = async () => {
    if (compareIds.length !== 2) return;
    try {
      const [d1, d2] = await Promise.all(compareIds.map(id => api.get(`/evaluations/${id}`).then(r => r.data)));
      setCompareData([d1, d2]);
      setShowCompare(true);
      setSelectedEval(null);
    } catch { toast.error("Failed to load evaluations for comparison"); }
  };

  const inputCls = "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 mt-1 focus:border-amber-400 focus:ring-amber-400 placeholder:text-slate-400 dark:placeholder:text-slate-500";

  return (
    <div className="space-y-6" data-testid="evaluator-page">
      <OnboardingBanner pageKey="evaluator" />

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-manrope text-2xl md:text-3xl font-semibold text-slate-900 dark:text-amber-400 tracking-tight flex items-center gap-2">
            <Sparkle size={28} className="text-amber-500" weight="duotone" />
            Property Evaluator
          </h1>
          <p className="text-slate-500 dark:text-slate-200 text-sm mt-1">AI-powered investment analysis · live Airbnb market data</p>
        </div>
        {/* Hidden on unlimited plans: admin carries 999999, paid plans carry -1 */}
        {user && typeof user.eval_credits === "number" && user.eval_credits !== 999999 && user.eval_credits >= 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-center">
            <p className="text-amber-600 font-mono text-lg font-bold">{user.eval_credits}</p>
            <p className="text-amber-500 text-[10px] uppercase tracking-widest font-semibold">Credits Left</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        {[["new", "New Evaluation"], ["history", "History"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === k ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}>
            {k === "history" && <Clock size={13} className="inline mr-1.5 -mt-0.5" />}{label}
          </button>
        ))}
      </div>

      {/* ── NEW EVALUATION TAB ── */}
      {tab === "new" && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-2xl shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-manrope text-base font-semibold text-slate-800 dark:text-white">Property Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold mb-2 block">How are you acquiring this property?</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ACQUISITION_TYPES.map(({ id, label, icon: Icon, desc, color }) => {
                    const active = acqType === id;
                    const colors = { amber: active ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-amber-200", sky: active ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:border-sky-200", purple: active ? "border-purple-400 bg-purple-50" : "border-slate-200 hover:border-purple-200" };
                    const iconColor = { amber: active ? "text-amber-500" : "text-slate-400", sky: active ? "text-sky-500" : "text-slate-400", purple: active ? "text-purple-500" : "text-slate-400" };
                    return (
                      <button key={id} onClick={() => setAcqType(id)} className={`rounded-xl border-2 p-3 text-left transition-all ${colors[color]}`}>
                        <Icon size={20} className={`mb-1.5 ${iconColor[color]}`} weight={active ? "fill" : "regular"} />
                        <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{label}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">State</Label>
                  <Select value={form.state} onValueChange={v => setForm(f => ({...f, state: v, city: ""}))}>
                    <SelectTrigger className={`${inputCls} mt-1`}><SelectValue placeholder="Select state…" /></SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 max-h-72 overflow-y-auto">
                      {Object.keys(INDIA_CITIES).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">City</Label>
                  <Select value={form.city} onValueChange={v => setForm(f => ({...f, city: v}))} disabled={!form.state}>
                    <SelectTrigger className={`${inputCls} mt-1`}><SelectValue placeholder={form.state ? "Select city…" : "Pick state first"} /></SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 max-h-72 overflow-y-auto">
                      {(INDIA_CITIES[form.state] || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Neighbourhood</Label>
                  <Input value={form.neighbourhood} onChange={e => setForm(f => ({...f, neighbourhood: e.target.value}))} className={inputCls} placeholder="Bandra, Calangute..." />
                </div>
                <div>
                  <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Pin Code</Label>
                  <Input value={form.pincode} onChange={e => setForm(f => ({...f, pincode: e.target.value}))} className={inputCls} placeholder="400050" />
                </div>
                <div>
                  <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Property Type</Label>
                  <Select value={form.property_type} onValueChange={v => setForm(f => ({...f, property_type: v}))}>
                    <SelectTrigger className="bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                      {PROPERTY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Bedrooms</Label>
                  <Input type="number" min={1} value={form.bedrooms} onChange={e => setForm(f => ({...f, bedrooms: e.target.value === "" ? "" : parseInt(e.target.value) || 1}))} className={inputCls} placeholder="e.g. 2" />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Expected Nightly Rate (INR) <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Input type="number" value={form.nightly_rate || ""} onChange={e => setForm(f => ({...f, nightly_rate: parseFloat(e.target.value) || 0}))} className={inputCls} placeholder="Leave blank for AI to estimate" />
                </div>
              </div>

              {acqType === "purchase" && (
                <div>
                  <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Purchase Price (INR)</Label>
                  <p className="text-[10px] text-slate-400 mt-0.5">Total cost to buy the property</p>
                  <Input type="number" value={form.purchase_price} onChange={e => setForm(f => ({...f, purchase_price: e.target.value === "" ? "" : parseFloat(e.target.value) || 0}))} className={inputCls} placeholder="e.g. 50,00,000" />
                </div>
              )}
              {acqType === "rental" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Monthly Rent (INR)</Label>
                    <p className="text-[10px] text-slate-400 mt-0.5">Rent you pay to the property owner</p>
                    <Input type="number" value={form.monthly_rent} onChange={e => setForm(f => ({...f, monthly_rent: parseFloat(e.target.value) || 0}))} className={inputCls} placeholder="25,000" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Security Deposit</Label>
                    <p className="text-[10px] text-slate-400 mt-0.5">How many months of rent as deposit?</p>
                    <Select value={String(form.security_deposit_months)} onValueChange={v => setForm(f => ({...f, security_deposit_months: parseInt(v)}))}>
                      <SelectTrigger className="bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                        {[1,2,3,4,5,6,10,12].map(n => <SelectItem key={n} value={String(n)}>{n} month{n > 1 ? "s" : ""} {form.monthly_rent ? `(₹${(form.monthly_rent * n).toLocaleString("en-IN")})` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {acqType === "lease" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Lease Amount (INR)</Label>
                    <p className="text-[10px] text-slate-400 mt-0.5">Lump sum paid upfront to the owner</p>
                    <Input type="number" value={form.lease_amount} onChange={e => setForm(f => ({...f, lease_amount: parseFloat(e.target.value) || 0}))} className={inputCls} placeholder="10,00,000" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Lease Duration</Label>
                    <p className="text-[10px] text-slate-400 mt-0.5">How many years is the lease agreement?</p>
                    <Select value={String(form.lease_duration_years)} onValueChange={v => setForm(f => ({...f, lease_duration_years: parseInt(v)}))}>
                      <SelectTrigger className="bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                        {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} year{n > 1 ? "s" : ""} {form.lease_amount ? `(₹${Math.round(form.lease_amount / (n * 12)).toLocaleString("en-IN")}/mo effective)` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold mb-2 block">Amenities</Label>
                <div className="flex flex-wrap gap-1.5">
                  {AMENITIES_LIST.map(a => (
                    <button key={a} onClick={() => toggleAmenity(a)}
                      className={`px-2 py-0.5 rounded-lg text-[11px] transition-colors font-medium ${form.amenities.includes(a) ? "bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-700" : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600"}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              <ImageUploader images={images} onAdd={handleAddImages} onRemove={handleRemoveImage} />

              <button onClick={handleEvaluate} disabled={loading}
                className="w-full bg-amber-400 text-white hover:bg-amber-500 disabled:opacity-50 font-semibold rounded-xl px-6 py-3 text-sm flex items-center justify-center gap-2 transition-colors shadow-sm">
                {loading
                  ? <><div className="spinner" /> Fetching live data &amp; analyzing...</>
                  : <><MagnifyingGlass size={16} weight="bold" /> Evaluate Property</>}
              </button>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {result && !result.error ? (
              <ResultsPanel result={result} acqType={result.acquisition_type || acqType} />
            ) : !loading ? (
              <div className="flex items-center justify-center h-64 bg-white dark:bg-slate-800 border border-slate-200 rounded-2xl shadow-sm">
                <div className="text-center">
                  <Lightning size={48} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">Select acquisition type and fill in details</p>
                  <p className="text-slate-400 dark:text-slate-300 text-xs mt-1">Optionally upload photos for AI vision analysis</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-white dark:bg-slate-800 border border-slate-200 rounded-2xl ai-shimmer shadow-sm">
                <div className="text-center">
                  <div className="spinner mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-200 text-sm">Searching live Airbnb listings &amp; analyzing...</p>
                  <p className="text-slate-400 text-xs mt-1">{images.some(im => im.done) ? "Processing images + real market data" : "Fetching real listings in area"}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === "history" && (
        <div className="space-y-4">
          {showCompare && compareData.length === 2 ? (
            <ComparePanel data={compareData} onClose={() => { setShowCompare(false); setCompareIds([]); setCompareData([]); }} />
          ) : (
            <div className={selectedEval ? "grid md:grid-cols-[340px,1fr] gap-4 items-start" : "space-y-4"}>

              {/* List + filters */}
              <div className="space-y-3">
                <div className="flex gap-2 flex-wrap items-center">
                  <Select value={filterCity} onValueChange={v => { setFilterCity(v); setFilterArea("all"); }}>
                    <SelectTrigger className="w-36 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs h-9">
                      <SelectValue placeholder="All Cities" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                      <SelectItem value="all">All Cities</SelectItem>
                      {availableCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <Select value={filterArea} onValueChange={setFilterArea} disabled={availableAreas.length === 0}>
                    <SelectTrigger className="w-36 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs h-9">
                      <SelectValue placeholder="All Areas" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                      <SelectItem value="all">All Areas</SelectItem>
                      {availableAreas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  {compareIds.length > 0 && (
                    <div className="flex items-center gap-2 ml-auto">
                      {compareIds.length === 2 && (
                        <button onClick={handleCompare}
                          className="flex items-center gap-1.5 bg-amber-400 text-white hover:bg-amber-500 font-semibold rounded-xl px-3 py-1.5 text-xs transition-colors">
                          <ArrowsLeftRight size={13} /> Compare
                        </button>
                      )}
                      <button onClick={() => setCompareIds([])} className="text-xs text-slate-400 hover:text-red-400 transition-colors px-1">
                        Clear {compareIds.length}/2
                      </button>
                    </div>
                  )}
                </div>

                {historyLoading ? (
                  <div className="flex items-center justify-center h-48 bg-white dark:bg-slate-800 border border-slate-200 rounded-2xl">
                    <div className="spinner" />
                  </div>
                ) : filteredHistory.length === 0 ? (
                  <div className="flex items-center justify-center h-48 bg-white dark:bg-slate-800 border border-slate-200 rounded-2xl">
                    <div className="text-center">
                      <Clock size={40} className="text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-400 text-sm">{history.length === 0 ? "No evaluations yet" : "No evaluations match filters"}</p>
                    </div>
                  </div>
                ) : filteredHistory.map(ev => (
                  <HistoryCard key={ev.id} ev={ev}
                    selected={selectedEval?.id === ev.id}
                    onView={() => handleViewHistory(ev)}
                    onDelete={handleDeleteHistory}
                    compareChecked={compareIds.includes(ev.id)}
                    onCompareToggle={() => toggleCompare(ev.id)}
                  />
                ))}
              </div>

              {/* Detail panel */}
              {selectedEval && (
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-800 dark:text-white">
                        {[selectedEval.neighbourhood || selectedEval.property_details?.neighbourhood, selectedEval.city || selectedEval.property_details?.city].filter(Boolean).join(", ")}
                      </p>
                      <p className="text-slate-400 text-xs mt-0.5">
                        {selectedEval.property_details?.property_type} · {selectedEval.property_details?.bedrooms} BR ·{" "}
                        {new Date(selectedEval.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </div>
                    <button onClick={() => setSelectedEval(null)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0">
                      <X size={16} />
                    </button>
                  </div>
                  <ResultsPanel result={selectedEval.result} acqType={selectedEval.property_details?.acquisition_type || "purchase"} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
